import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import { toast } from "react-toastify";
import {
    BoxTitle,
    BoxContainer,
    BoxHead,
    Underline,
} from "../../Components/Styles/PageStyles";
import { useSelector } from "react-redux";
import NotFoundModel from "../../Components/NotFoundModel";
import DeleteBank from "../../Components/BankDetails/DeleteBank";
import BankListTable from "../../Components/Landlords/BankListTable";
import AddBankDetails from "../../Components/Landlords/AddBankDetails";
import { IoChevronBackOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import SideModal from "../../Components/SideModal";
import EditBankDetails from "../../Components/Landlords/EditBankDetails";

const LandlordBankDetail = () => {
    const location = useLocation();
    const landlordId = location?.state?.leaseDetails?.id || location?.state?.leaseDetails
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const [addBankDetails, setAddBankDetails] = useState(false);
    const [isDeleteBank, setIsDeleteBank] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [selectedData, setSelectedData] = useState(null);
    const [isEditBank, setIsEditBank] = useState(false);

    const fetchFunction = async () => {
        const response = await axios.get(`${Config.apiUrl}/client/list/landlord/banks?landlordId=${landlordId}`, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === true) {
            setApiData(res?.landlordBanks);
        }
    };

    const getError = (res) => {
        toast.error("Failed to fetch bank details");
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        getListMutate();
    }, [getListMutate]);

    //------- Add Bank Account -------
    const AddBankAccountFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/client/add/landlord/bank`, values, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
    };

    const restorePaymentSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg);
            setAddBankDetails(false);
            getListMutate();
        } else {
            toast.error(data?.data?.msg);
        }
    };

    const restorePaymentError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occurred");
    };

    const { isLoading: addBankAccountLoading, mutate: addBankAccountMutate } =
        useMutation(AddBankAccountFunction, {
            onSuccess: restorePaymentSuccess,
            onError: restorePaymentError,
        });
    const handleAddBankClose = () => {
        setAddBankDetails(false);
    };

    //------- Delete Bank -------
    const deleteBankFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/client/delete/landlord/bank`, values, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/json",
            },
        });
    };

    const onDeleteBankSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Bank account deleted successfully");
            getListMutate();
            setIsDeleteBank(false);
        } else {
            toast.error(data?.data?.msg || "An Error Occurred");
        }
    };

    const onDeleteBankError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occurred");
    };

    const { isLoading: deleteBankLoading, mutate: deleteBankMutate } =
        useMutation(deleteBankFunction, {
            onSuccess: onDeleteBankSuccess,
            onError: onDeleteBankError,
        });

    const handleDeleteModalClose = () => {
        setIsDeleteBank(false);
    };
    const handleEditModalClose = () => {
        setIsEditBank(false);
    };
    const handleBackClick = () => {
        navigate("/landlords");
    };

    //------- Edit Bank -------

    const editBankFunction = async (payload) => {
        return await axios.post(`${Config.apiUrl}/client/edit/landlord/bank`, payload, {
            headers: {
                authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
    };

    const { isLoading: editBankLoading, mutate: editBankMutate } = useMutation(
        editBankFunction,
        {
            onSettled: () => {
                setIsEditBank(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );


    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    <BackWrapper>
                        <BackButton onClick={handleBackClick}>
                            <IoChevronBackOutline size={20} />
                        </BackButton>
                        <div >
                            <span className="flex items-center"> Bank Accounts</span>
                            <Underline />
                        </div>
                    </BackWrapper>

                </BoxTitle>
                <Filters setAddBankDetails={setAddBankDetails} />
            </BoxHead>
            {(isLoading || isRefetching) && <Loading />}

            {!error && !isLoading && (
                <TableWrapper>
                    <BankListTable
                        apiData={error ? [] : apiData}
                        setSelectedData={setSelectedData}
                        setIsDeleteBank={setIsDeleteBank}
                        setIsEditBank={setIsEditBank}
                    />
                </TableWrapper>
            )}
            {error && !isLoading && <NotFoundModel />}
            <SideModal
                isOpen={addBankDetails}
                onClose={handleAddBankClose}
                level="base"
            >
                <AddBankDetails
                    handleClose={handleAddBankClose}
                    addBankAccountLoading={addBankAccountLoading}
                    addBankAccountMutate={addBankAccountMutate}
                    landlordId={landlordId}
                />
            </SideModal>

            <SideModal
                isOpen={isEditBank}
                onClose={handleEditModalClose}
                level="base"
            >
                <EditBankDetails
                    handleClose={handleEditModalClose}
                    editBankLoading={editBankLoading}
                    editBankMutate={editBankMutate}
                    selectedData={selectedData}
                />
            </SideModal>

            {isDeleteBank && (
                <DeleteBank
                    deleteBankMutate={deleteBankMutate}
                    deleteBankLoading={deleteBankLoading}
                    setIsDeleteBank={setIsDeleteBank}
                    selectedData={selectedData}
                    handleClose={handleDeleteModalClose}
                />
            )}
        </BoxContainer>
    );
};

const Filters = ({ setAddBankDetails }) => {
    return (
        <AddBtn
            type="button"
            onClick={() => {
                setAddBankDetails(true);
            }}
        >
            Add Bank Account
        </AddBtn>
    );
};

const AddBtn = tw.button`border whitespace-nowrap border-gray-200 flex items-center justify-center text-white h-full rounded-md px-4 cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm transition-colors duration-200`;
const TableWrapper = tw.div`my-10 border bg-white rounded-md `;
const BackWrapper = tw.div`flex items-center`;
const BackButton = tw.button`hover:bg-gray-200 items-center -mt-1 p-1 rounded-md transition-colors`;
export default LandlordBankDetail;
